/**
 * Read the document head the server already wrote (`#apt-seo-ssr`) and keep it
 * for the landing route. Later client navigations ask `GET /api/seo/resolved`.
 *
 * Course detail is the exception: its server shell is still a weak derived title,
 * so `weak: true` (and `preferClientSeo` on that page) keeps the client copy.
 */

import { normalizePublicSeoPath } from "../../shared/seo/publicSeoDetailPath";

export type SsrSeoPayload = {
  path: string;
  title: string;
  description: string;
  canonical: string;
  robots: string;
  ogTitle: string;
  ogDescription: string;
  twitterTitle: string;
  twitterDescription: string;
  overrideMask: string[];
  source: "registry" | "auto";
  weak?: boolean;
};

type SsrSeoCache = { payload: SsrSeoPayload; path: string } | null;

let cached: SsrSeoCache | undefined;

export function resetSsrSeoCacheForTests(): void {
  cached = undefined;
}

export function readSsrSeo(): SsrSeoCache {
  if (cached !== undefined) return cached;
  if (typeof document === "undefined") {
    cached = null;
    return null;
  }
  const node = document.getElementById("apt-seo-ssr");
  if (!node) {
    cached = null;
    return null;
  }
  try {
    const parsed = JSON.parse(node.textContent || "") as SsrSeoPayload;
    const path = normalizePublicSeoPath(parsed.path || "");
    cached = {
      payload: { ...parsed, path },
      path,
    };
  } catch {
    cached = null;
  }
  node.remove();
  return cached;
}

/** Payload for the route the HTML was rendered for. Weak course shells are ignored. */
export function ssrSeoForCurrentPath(pathname: string): SsrSeoPayload | null {
  const read = readSsrSeo();
  if (!read) return null;
  if (normalizePublicSeoPath(pathname) !== normalizePublicSeoPath(read.path)) {
    return null;
  }
  if (read.payload.weak) return null;
  return read.payload;
}
